import type { ListProfile } from '$lib/api/listprofile';

/**
 * Turning a profile into filters a customer can accept, reject or edit.
 *
 * # All three are ticked, and industry was the argument
 *
 * The three do not compose gently. Measured against the corpus for the first
 * real list: industry alone left 17,378 companies the customer did not have,
 * size alone 12,740 — and both together 921. Only 8% of that customer's own
 * book satisfied both, so the intersection excludes 92% of the companies that
 * defined the profile. On that reading industry started unticked, as the axis
 * that collapses the volume.
 *
 * That was the wrong thing to optimise. Measured down the funnel the profile
 * actually produces for that customer:
 *
 *   region only                 504,305
 *   region + size 10+            81,760
 *   region + size + industry      4,862
 *
 * The ceiling on a priced selection is 30,000 — see maxQuotableRecords. So
 * without industry the customer lands four times over it, and the bar shows a
 * count with no price and an order button that cannot be pressed. A selection
 * nobody can buy is worse than one that is narrower than their whole market,
 * and most lists are diverse enough that this is the normal case rather than
 * an edge.
 *
 * # The collapse is still real, so it stays undoable
 *
 * Unticking industry is one click, and the count moves the instant it happens.
 * That is the escape for the customer whose market genuinely is broader than
 * ten categories: they widen it and narrow some other way. The default is set
 * for the case that otherwise dead-ends, not because the intersection is a
 * complete description of anybody's market.
 */

/** The three axes, which are alternatives rather than a set to combine. */
export type Axis = 'industry' | 'size' | 'region';

export interface IndustrySuggestion {
  centroids: { id: number; name: string }[];

  /**
   * How many of the categorised companies these account for, and out of how
   * many.
   *
   * # Derived here rather than read off the response
   *
   * The service sends `covered` and it is the same number — the sum of the
   * companies in the categories it returned. Summing them here instead costs
   * nothing and unbinds this screen from the service's version: an older
   * deployment that does not send the field yet renders a correct sentence
   * rather than "NaN of 1,319", which is what it did.
   *
   * That is worth doing for any field a client can work out for itself. A
   * missing number should degrade to a right answer, or to no answer — never
   * to arithmetic on `undefined`.
   */
  covered: number;
  categorised: number;

  /** `covered` as a fraction of `categorised`, and 0 when there is nothing to divide. */
  share: number;
}

export interface SizeSuggestion {
  /** The floor, in staff. Null when the bands give no honest one. */
  min: number | null;

  /**
   * The ceiling, which is almost always null. See `sizeCeiling`.
   */
  max: number | null;

  median: number;
  corpusMedian: number;
}

export interface RegionSuggestion {
  languages: string[];

  /** Empty when the list covers its language regions evenly, which is usual. */
  cantons: string[];
}

export interface Suggestions {
  industry: IndustrySuggestion | null;
  size: SizeSuggestion | null;
  region: RegionSuggestion | null;

  /** Which axes start ticked. */
  preselected: Axis[];
}

/**
 * The band boundaries, as the service names them.
 *
 * Parsed rather than hardcoded so that a band added on the server does not
 * silently fall out of the floor calculation here.
 */
function bandFloor(band: string): number | null {
  const match = /^(\d+)/.exec(band);
  return match ? Number(match[1]) : null;
}

/**
 * The lowest headcount worth filtering on.
 *
 * The lowest band the list is not under-represented in — the first place going
 * up where the customer stops being unusual. For a supplier who never sells to
 * micro-businesses the 1-9 band comes back at a lift of about 0.1 and the one
 * above it at 1.4, so the floor lands at ten.
 *
 * # Why not the first quartile
 *
 * Because it cuts a quarter of the customer's own book by construction,
 * whatever the shape of it. On the first real list the quartile happened to
 * land on the number the customer themselves named, which read as a
 * confirmation and was a coincidence of that distribution.
 */
export function sizeFloor(profile: ListProfile): number | null {
  for (const band of profile.size.bands) {
    if (band.lift >= 1) return bandFloor(band.band);
  }
  return null;
}

/**
 * How many companies a ceiling would have to be inferred from.
 *
 * Small, because the top of a Swiss size distribution is thin for everybody and
 * an upper bound read off a handful of companies is not a finding.
 */
const minCeilingEvidence = 20;

/**
 * The highest headcount worth filtering on, which is almost always none.
 *
 * A customer's size requirement is normally a floor. The bands above their
 * range thin out because the corpus thins out, not because they refuse the
 * work — and on the first real list the two top bands held four and seven
 * companies between them, which is nothing to draw a boundary from.
 *
 * So a ceiling is only offered when the bands above the floor genuinely fall
 * away *and* there are enough companies up there to mean it.
 */
export function sizeCeiling(profile: ListProfile, floor: number | null): number | null {
  if (floor === null) return null;

  const above = profile.size.bands.filter((b) => {
    const start = bandFloor(b.band);
    return start !== null && start > floor;
  });

  // Walk down from the top, collecting the bands the list is clearly absent
  // from, and stop at the first one it is present in.
  let evidence = 0;
  let ceiling: number | null = null;

  for (let i = above.length - 1; i >= 0; i--) {
    const band = above[i];
    if (band.lift >= 0.5) break;

    evidence += band.companies;
    ceiling = bandFloor(band.band);
  }

  if (ceiling === null || evidence < minCeilingEvidence) return null;

  // The ceiling is the last headcount *below* the band the list is absent from.
  return ceiling - 1;
}

/** Turn a profile into the filters to offer. */
export function suggestionsFrom(profile: ListProfile): Suggestions {
  const covered = profile.categories.reduce((total, c) => total + (c.companies ?? 0), 0);

  const industry: IndustrySuggestion | null =
    profile.diffuse || profile.categories.length === 0
      ? null
      : {
          centroids: profile.categories.map((c) => ({ id: c.centroid_id, name: c.name })),
          covered,
          categorised: profile.categorised,
          // Guarded: a list where nothing could be categorised has no share to
          // report, and dividing by it would put NaN on the screen.
          share: profile.categorised > 0 ? covered / profile.categorised : 0
        };

  const floor = profile.size.unreliable ? null : sizeFloor(profile);
  const size: SizeSuggestion | null =
    floor === null
      ? null
      : {
          min: floor,
          max: sizeCeiling(profile, floor),
          median: profile.size.median,
          corpusMedian: profile.size.corpus_median
        };

  const region: RegionSuggestion | null =
    profile.region.unreliable || profile.region.served.length === 0
      ? null
      : {
          languages: profile.region.served,
          // Only when a canton actually stood out. Uniform means the list
          // covers its language regions evenly, and a canton list there would
          // narrow the selection to no purpose.
          cantons: profile.region.uniform ? [] : profile.region.cantons.map((c) => c.canton)
        };

  // Everything we found. Region and size together leave the ordinary customer
  // above the pricing ceiling, so a screen that starts with industry unticked
  // starts unbuyable — see the note above.
  const preselected: Axis[] = [];
  if (region) preselected.push('region');
  if (size) preselected.push('size');
  if (industry) preselected.push('industry');

  return { industry, size, region, preselected };
}
